import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Employee } from '../types';
import { useAuth } from './AuthContext';
import PasswordResetModal from './PasswordResetModal';
import { getPendingVacationRequests } from '../services/apiService';

const pendingBadgeStyle: React.CSSProperties = {
  display: 'inline-block',
  marginLeft: '8px',
  padding: '2px 8px',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: '#856404',
  backgroundColor: '#fff3cd',
  border: '1px solid #ffeeba',
  borderRadius: '10px',
  verticalAlign: 'middle',
  whiteSpace: 'nowrap',
};

const EmployeeList: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [pendingCounts, setPendingCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const response = await axios.get('/api/overtime');
        setEmployees(response.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching employees:', err);
        setError('Fehler beim Laden der Mitarbeiter. Bitte später erneut versuchen.');
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  useEffect(() => {
    if (!user?.isAdmin) {
      return;
    }
    const fetchPendingRequests = async () => {
      try {
        const pending = await getPendingVacationRequests();
        const counts = pending.reduce<Record<string, number>>((acc, request) => {
          acc[request.employeeUserId] = (acc[request.employeeUserId] || 0) + 1;
          return acc;
        }, {});
        setPendingCounts(counts);
      } catch (err) {
        console.error('Error fetching pending vacation requests:', err);
      }
    };

    fetchPendingRequests();
  }, [user?.isAdmin]);

  useEffect(() => {
    const handleClickOutside = () => {
      setOpenMenuId(null);
    };

    if (openMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [openMenuId]);

  const handleMenuToggle = (e: React.MouseEvent, userId: string) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === userId ? null : userId);
  };

  const handleResetPasswordClick = (e: React.MouseEvent, employeeName: string) => {
    e.stopPropagation();
    setSelectedEmployee(employeeName);
    setOpenMenuId(null);
  };

  if (loading) {
    return <div className="loading">Mitarbeiter werden geladen...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  const isEmployeeActive = (employee: Employee): boolean => {
    if (!employee.lastDayOfContract) {
      return true;
    }
    const lastDay = new Date(employee.lastDayOfContract);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return lastDay >= today;
  };

  const activeEmployees = employees.filter(isEmployeeActive);
  const inactiveEmployees = employees.filter(employee => !isEmployeeActive(employee));

  const renderEmployeeTable = (employeeList: Employee[], title: string) => {
    if (employeeList.length === 0) {
      return null;
    }

    return (
      <div style={{ marginBottom: '40px' }}>
        <h2>{title}</h2>
        <div style={{ overflow: 'visible' }}>
          <table className="employee-table" style={{ overflow: 'visible' }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Erster Arbeitstag</th>
                <th>Letzter Arbeitstag</th>
                {user?.isAdmin && <th style={{ textAlign: 'right' }}>Aktionen</th>}
              </tr>
            </thead>
            <tbody style={{ overflow: 'visible' }}>
              {employeeList.map((employee) => (
                <tr
                  key={employee.userId}
                  onClick={() => navigate(`/employee/${encodeURIComponent(employee.name)}`)}
                  style={{ overflow: 'visible' }}
                >
                  <td data-label="Name">
                    {employee.name}
                    {user?.isAdmin && pendingCounts[employee.userId] > 0 && (
                      <span
                        style={pendingBadgeStyle}
                        title={`${pendingCounts[employee.userId]} ausstehende Urlaubsanträge`}
                      >
                        {pendingCounts[employee.userId]} offen
                      </span>
                    )}
                  </td>
                  <td data-label="Erster Arbeitstag">{new Date(employee.beginOfContract).toLocaleDateString()}</td>
                  <td data-label="Letzter Arbeitstag">
                    {employee.lastDayOfContract
                      ? new Date(employee.lastDayOfContract).toLocaleDateString()
                      : '-'}
                  </td>
                  {user?.isAdmin && (
                    <td data-label="Aktionen" style={{ textAlign: 'right', overflow: 'visible' }}>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <button
                          onClick={(e) => handleMenuToggle(e, employee.userId)}
                          className="action-button"
                          style={{
                            padding: '5px 10px',
                            fontSize: '16px',
                            backgroundColor: 'white',
                            color: '#495057',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            boxShadow: 'none',
                          }}
                        >
                          ⋮
                        </button>
                        {openMenuId === employee.userId && (
                          <div
                            style={{
                              position: 'absolute',
                              right: '0',
                              top: 'calc(100% + 4px)',
                              backgroundColor: 'white',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                              zIndex: 1000,
                              minWidth: '180px',
                            }}
                          >
                            <button
                              onClick={(e) => handleResetPasswordClick(e, employee.name)}
                              style={{
                                width: '100%',
                                padding: '10px 15px',
                                textAlign: 'left',
                                border: 'none',
                                backgroundColor: 'transparent',
                                cursor: 'pointer',
                                fontSize: '14px',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#f8f9fa';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              Passwort zurücksetzen
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="container">
      {employees.length === 0 ? (
        <p>Keine Mitarbeiter gefunden.</p>
      ) : (
        <>
          {renderEmployeeTable(activeEmployees, 'Aktive Mitarbeiter')}
          {renderEmployeeTable(inactiveEmployees, 'Inaktive Mitarbeiter')}
        </>
      )}

      {selectedEmployee && (
        <PasswordResetModal
          employeeName={selectedEmployee}
          onClose={() => setSelectedEmployee(null)}
        />
      )}
    </div>
  );
};

export default EmployeeList;